import { type ComponentType, memo } from 'react';

import { type Board, type CardSize, type Clip } from '@air/api/types';

import { type UseGalleryAssetsParams } from '~/components/Gallery/useGalleryAssets';
import { type UseGalleryBoardParams } from '~/components/Gallery/useGalleryBoards';
import { type UseGalleryFilesParams } from '~/components/Gallery/useGalleryFiles';
import { PrivateBoardNullState } from '~/components/PrivateBoard/PrivateBoardNullState';
import { type SelectableGalleryViewProps } from '~/components/SelectableGalleryView/SelectableGalleryView';
import { GalleryView, type GalleryViewProps } from '~/components/v2/components/shared/gallery/GalleryView';
import { usePermissionedVisibleCustomFieldsLength } from '~/components/v2/hooks/usePermissionedVisibleCustomFieldsLength';
import { useGroupedGallerySelectedItems } from '~/hooks/useGroupedGallerySelectedItems';
import { type UseGalleryViewItemsReturn } from '~/swr-hooks/gallery/types';

export type PrivateGalleryViewProps = {
  className?: string;
  itemsData: UseGalleryViewItemsReturn<Board, Clip>;
  scrollElementRef: SelectableGalleryViewProps['scrollElementRef'];
  NullState?: ComponentType;
  renderBoard: NonNullable<UseGalleryBoardParams<Board>['renderBoard']>;
  renderAsset: NonNullable<UseGalleryAssetsParams<Clip>['renderAsset']>;
  renderFile: NonNullable<UseGalleryFilesParams<Clip>['renderFile']>;
  cardSize?: CardSize;
  showTitleAndMetadata?: boolean;
  useCustomFieldsLength?: GalleryViewProps<Board, Clip>['useCustomFieldsLength'];
};

export const PrivateGalleryView = memo(
  ({
    className,
    itemsData,
    scrollElementRef,
    renderBoard,
    renderAsset,
    renderFile,
    NullState = PrivateBoardNullState,
    cardSize,
    showTitleAndMetadata,
    useCustomFieldsLength = usePermissionedVisibleCustomFieldsLength,
  }: PrivateGalleryViewProps) => (
    <GalleryView
      cardSize={cardSize}
      className={className}
      itemsData={itemsData}
      scrollElementRef={scrollElementRef}
      renderBoard={renderBoard}
      renderAsset={renderAsset}
      renderFile={renderFile}
      NullState={NullState}
      renderHeader={undefined}
      showTitleAndMetadata={showTitleAndMetadata}
      useCustomFieldsLength={useCustomFieldsLength}
      // This surface mounts ONE gallery, so it registers its own items as the selectable set.
      useSelectableItems={useGroupedGallerySelectedItems}
    />
  ),
);

PrivateGalleryView.displayName = 'PrivateGalleryView';
